import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

/**
 * Props:
 * - onDetected(isbn13: string): appelé quand un ISBN-13 valide (978/979) est détecté
 * - autoStop: boolean (default true) : stoppe le scan au premier ISBN trouvé
 */
export default function BarcodeScanner({ onDetected, autoStop = true }) {
    const videoRef = useRef(null);
    const controlsRef = useRef(null);

    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState("");
    const [running, setRunning] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | requesting | scanning | error
    const [lastValue, setLastValue] = useState("");

    const hints = useMemo(() => {
        const m = new Map();
        m.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13]);
        m.set(DecodeHintType.TRY_HARDER, true);
        return m;
    }, []);

    const reader = useMemo(() => new BrowserMultiFormatReader(hints, 200), [hints]);

    function isValidISBN13(ean13) {
        if (!/^\d{13}$/.test(ean13)) return false;
        // ISBN-13 en librairie commence généralement par 978 ou 979
        return ean13.startsWith("978") || ean13.startsWith("979");
    }

    async function loadDevices() {
        setStatus("requesting");
        try {
            // Astuce: demander une permission une fois aide à obtenir les labels des caméras
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach((t) => t.stop());

            const list = await BrowserMultiFormatReader.listVideoInputDevices();
            setDevices(list);

            // Choix par défaut: caméra "arrière" si on trouve un indice, sinon la première
            const preferred =
                list.find((d) => /back|rear|environment/i.test(d.label)) || list[0];

            setDeviceId(preferred?.deviceId || "");
            setStatus("idle");
        } catch (e) {
            console.error(e);
            setStatus("error");
        }
    }

    async function start() {
        if (!deviceId || !videoRef.current) return;
        setStatus("scanning");
        setRunning(true);

        const constraints = {
            audio: false,
            video: {
                facingMode: { ideal: "environment" },
                width: { ideal: 1280 },
                height: { ideal: 720 },
                // sur certains devices, tu peux tenter:
                // focusMode: "continuous"  // (pas standard partout)
            },
        };

        // Lance le scan en continu
        controlsRef.current = await reader.decodeFromConstraints(
            constraints,
            videoRef.current,
            (result, err) => {
                if (result) {
                    const text = result.getText();

                    if (isValidISBN13(text)) {
                        onDetected?.(text);
                        setLastValue(text);
                        if (autoStop) stop();
                    }
                }
            }
        );
        /*controlsRef.current = await reader.decodeFromVideoDevice(
            deviceId,
            videoRef.current,
            (result, err) => {
                if (result) {
                    const text = result.getText();
                    if (isValidISBN13(text)) {
                        setLastValue(text);
                        onDetected?.(text);
                        if (autoStop) stop();
                    }
                }
                // err est fréquent (pas de code détecté sur cette frame) => ignorer
            }
        );
    }*/
    }
    function stop() {
        try {
            controlsRef.current?.stop();
        } catch { }
        controlsRef.current = null;

        reader.reset();
        setRunning(false);
        setStatus("idle");
    }

    useEffect(() => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setStatus("error");
            return;
        }
        loadDevices();

        return () => {
            // cleanup à l’unmount
            try {
                controlsRef.current?.stop();
            } catch { }
            //reader.reset();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Si on change de caméra en cours, on redémarre proprement
    useEffect(() => {
        if (!running) return;
        (async () => {
            stop();
            await start();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    return (
        <div style={{ display: "grid", gap: 12, maxWidth: 640 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", color: '#444' }}>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    Caméra
                    <select
                        style={{ color: '#444', backgroundColor: '#fff' }}
                        value={deviceId}
                        onChange={(e) => setDeviceId(e.target.value)}
                        disabled={status === "requesting" || devices.length === 0}
                    >
                        {devices.map((d) => (
                            <option key={d.deviceId} value={d.deviceId}>
                                {d.label || d.deviceId}
                            </option>
                        ))}
                    </select>
                </label>

                {!running ? (
                    <button style={{ borderRadius: 6, border: '1px solid #fff', color: '#fff', padding: 4 }} onClick={start} disabled={!deviceId || status === "requesting" || status === "error"}>
                        Démarrer
                    </button>
                ) : (
                    <button style={{ borderRadius: 6, border: '1px solid #fff', color: '#fff', padding: 4 }} onClick={stop}>Arrêter</button>
                )}

                <span style={{ padding: "4px 10px" }}>
                    {status === "requesting" && "Demande d’accès caméra…"}
                    {status === "scanning" && "Scan en cours…"}
                    {status === "idle" && "Prêt"}
                    {status === "error" && "Erreur caméra / permissions"}
                </span>
            </div>

            <video
                ref={videoRef}
                style={{ width: "100%", borderRadius: 12, border: "1px solid #ddd" }}
                playsInline
                muted
            />

            <div style={{ fontSize: 14 }}>
                <div>
                    Dernière valeur lue : <b>{lastValue || "—"}</b>
                </div>

            </div>
        </div>
    );
}